import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { AUTH_SERVICE_TOKEN, AuthService } from '../auth-service/auth.service';

import {
  AcceptDigestRequest,
  AiConversationTopic,
  AiStreamError,
  AiStreamErrorKind,
  AskQuestionRequest,
  ConversationContextDto,
  DigestDto,
  DocumentUploadResponse,
} from '../../model';

/**
 * Client for the topic-scoped advisor↔AI chat (`${apiEndpoint}/ai/chat`).
 *
 * JSON calls go through {@link HttpClient} (the `AuthInterceptor` adds the Bearer token). The two
 * streaming endpoints return a WebFlux `Flux<String>` (raw text chunks, not SSE), which `HttpClient`
 * cannot surface incrementally — so they are consumed with `fetch()` + a `ReadableStream` reader and
 * exposed as an `Observable<string>` that emits each decoded delta. `fetch()` is NOT tokenised by the
 * interceptor, so the Bearer header is attached manually.
 */
@Injectable({ providedIn: 'root' })
export class AiConversationService {
  private readonly base = `${environment.apiEndpoint}/ai/chat`;

  constructor(
    private readonly http: HttpClient,
    @Inject(AUTH_SERVICE_TOKEN) private readonly authService: AuthService,
  ) {}

  /** Get-or-create the scoped conversation (id + prior turns + any accepted digest). No AI turn. */
  public openTopic(topic: AiConversationTopic, subjectId: string): Observable<ConversationContextDto> {
    const params = new HttpParams().set('subjectId', subjectId);
    return this.http.get<ConversationContextDto>(`${this.base}/topic/${topic}`, { params });
  }

  /** The currently accepted smart prompt for the scope, or a 404 error if none exists. */
  public getDigest(topic: AiConversationTopic, subjectId: string): Observable<DigestDto> {
    return this.http.get<DigestDto>(`${this.base}/digest/${topic}/${subjectId}`);
  }

  /** Persists the accepted context and triggers the async analysis re-run. Returns immediately. */
  public acceptDigest(topic: AiConversationTopic, subjectId: string, contextText: string): Observable<DigestDto> {
    const body: AcceptDigestRequest = { contextText };
    return this.http.put<DigestDto>(`${this.base}/digest/${topic}/${subjectId}`, body);
  }

  /** Deletes the scoped conversation + its history so the next open starts fresh ("Neu starten"). */
  public resetConversation(topic: AiConversationTopic, subjectId: string): Observable<void> {
    const params = new HttpParams().set('subjectId', subjectId);
    return this.http.delete<void>(`${this.base}/topic/${topic}`, { params });
  }

  /**
   * Uploads a document (PDF/PNG/JPEG, max. 10 MB) into the conversation context. The backend ingests
   * it immediately (OCR if needed), so the next question already "knows" the file. Metered against
   * the advisor's AI wallet (429 when the quota is exhausted).
   */
  public uploadDocument(conversationId: string, file: File): Observable<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('document', file, file.name);
    return this.http.post<DocumentUploadResponse>(`${this.base}/${conversationId}/document`, formData);
  }

  /** Streams one chat answer token-by-token. Metered against the advisor's AI wallet. */
  public ask(conversationId: string, question: string): Observable<string> {
    const body: AskQuestionRequest = { question, conversationId };
    return this.stream(`${this.base}/question`, body);
  }

  /**
   * Streams the automatic opening message (contract identification + summary) of a fresh
   * CONTRACT_DISCUSSION conversation — same NDJSON delta stream as {@link ask}, no body. The server
   * is idempotent: for a conversation that is not a fresh contract discussion it returns an
   * immediately-completing empty stream.
   */
  public kickoff(conversationId: string): Observable<string> {
    return this.stream(`${this.base}/${conversationId}/kickoff`, null);
  }

  /** Streams the distilled draft smart prompt token-by-token. Stateless; nothing is saved yet. */
  public distill(conversationId: string): Observable<string> {
    return this.stream(`${this.base}/${conversationId}/distill`, null);
  }

  /**
   * Runs a POST that returns a text stream and emits each decoded chunk. Unsubscribing aborts the
   * request (teardown → `AbortController.abort()`). Errors are mapped to {@link AiStreamError}.
   */
  private stream(url: string, body: unknown | null): Observable<string> {
    return new Observable<string>((subscriber) => {
      const controller = new AbortController();
      let aborted = false;

      const run = async (): Promise<void> => {
        let token: string;
        try {
          token = await this.authService.getToken();
        } catch {
          if (!aborted) {
            subscriber.error(this.error('GENERIC'));
          }
          return;
        }

        let response: Response;
        try {
          response = await fetch(url, {
            method: 'POST',
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: body != null ? JSON.stringify(body) : null,
          });
        } catch {
          // AbortError lands here too — stay silent when we caused it.
          if (!aborted) {
            subscriber.error(this.error('GENERIC'));
          }
          return;
        }

        if (!response.ok) {
          const mapped = await this.mapResponseError(response);
          if (!aborted) {
            subscriber.error(mapped);
          }
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          if (!aborted) {
            subscriber.complete();
          }
          return;
        }

        // The streaming endpoint emits NDJSON (one JSON-encoded token per line). But tolerate a plain
        // text / buffered response too (e.g. a backend without the NDJSON media type deployed yet), so
        // newlines and formatting are never mangled. Decide by the response content type.
        const contentType = (response.headers.get('content-type') ?? '').toLowerCase();
        const ndjson = contentType.includes('ndjson') || contentType.includes('stream+json');
        const decoder = new TextDecoder();
        let buffer = '';
        const emitLine = (line: string): void => {
          const trimmed = line.trim();
          if (!trimmed) {
            return;
          }
          try {
            subscriber.next(JSON.parse(trimmed) as string);
          } catch {
            // Not valid JSON → treat as raw text (with its line break) rather than dropping it.
            subscriber.next(`${line}\n`);
          }
        };
        try {
          for (;;) {
            const { done, value } = await reader.read();
            if (aborted) {
              return;
            }
            if (done) {
              break;
            }
            const text = decoder.decode(value, { stream: true });
            if (!ndjson) {
              // Raw passthrough: keep the text exactly as sent (newlines, spaces, markdown).
              if (text) {
                subscriber.next(text);
              }
              continue;
            }
            buffer += text;
            let nl: number;
            while ((nl = buffer.indexOf('\n')) >= 0) {
              emitLine(buffer.slice(0, nl));
              buffer = buffer.slice(nl + 1);
            }
          }
          const tail = decoder.decode();
          if (!aborted) {
            if (ndjson) {
              (buffer + tail).split('\n').forEach(emitLine); // flush a final line without a newline
            } else if (tail) {
              subscriber.next(tail);
            }
            subscriber.complete();
          }
        } catch {
          // A mid-stream reader rejection (network drop) surfaces the same error kinds.
          if (!aborted) {
            subscriber.error(this.error('GENERIC'));
          }
        }
      };

      void run();

      return () => {
        aborted = true;
        controller.abort();
      };
    });
  }

  /** Maps a non-ok stream response to a {@link AiStreamError} kind (429/quota → QUOTA, 403 → NO_PLAN). */
  private async mapResponseError(response: Response): Promise<AiStreamError> {
    if (response.status === 429) {
      return this.error('QUOTA');
    }
    if (response.status === 403) {
      return this.error('NO_PLAN');
    }
    let code: string | undefined;
    try {
      const data = await response.clone().json();
      code = data?.code;
    } catch {
      // non-JSON body — fall through to GENERIC
    }
    return this.error(code === 'AI_QUOTA_EXCEEDED' ? 'QUOTA' : 'GENERIC');
  }

  private error(kind: AiStreamErrorKind): AiStreamError {
    return { kind };
  }
}
